import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    FiPlus, FiEdit2, FiTrash2, FiSearch, FiX,
    FiAlertCircle, FiMoreVertical
} from 'react-icons/fi';
import './HrProjectManagement.css';
import toast from 'react-hot-toast';
import ConfirmModal from './shared/ConfirmModal';

const api = axios.create({ baseURL: process.env.REACT_APP_BASE_URL });
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
});

const STATUS_META = {
    ACTIVE: { label: 'Active', color: '#16a34a', bg: '#dcfce7' },
    INACTIVE: { label: 'Inactive', color: '#64748b', bg: '#f1f5f9' },
    PLANNING: { label: 'Planning', color: '#d97706', bg: '#fef3c7' },
    IN_PROGRESS: { label: 'In Progress', color: '#2563eb', bg: '#dbeafe' },
    COMPLETED: { label: 'Completed', color: '#059669', bg: '#d1fae5' },
    ON_HOLD: { label: 'On Hold', color: '#ea580c', bg: '#ffedd5' },
    CANCELLED: { label: 'Cancelled', color: '#dc2626', bg: '#fee2e2' },
    MAINTENANCE: { label: 'Maintenance', color: '#d97706', bg: '#fef3c7' },
};

const HEALTH_COLOR = (v) => v >= 80 ? '#16a34a' : v >= 50 ? '#d97706' : '#dc2626';

const AVATAR_COLORS = [
    '#6366f1', '#f59e0b', '#10b981', '#3b82f6',
    '#ec4899', '#8b5cf6', '#14b8a6', '#f97316',
];

const EMPTY_PRODUCT = {
    productname: '', budget: '', description: '',
    status: 'ACTIVE', clientName: { name: '' }, manager_id: { id: '' }
};

function Avatar({ name, index, size = 30 }) {
    const color = AVATAR_COLORS[index % AVATAR_COLORS.length];
    const initials = name
        ? name.split(/[\s_]/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
        : '?';
    return (
        <div className="pm-avatar" style={{ width: size, height: size, background: color, fontSize: size * 0.36 }}>
            {initials}
        </div>
    );
}

// function HealthBar({ value }) {
//     const color = HEALTH_COLOR(value);
//     return (
//         <div className="pm-health-wrap">
//             <div className="pm-health-track">
//                 <div className="pm-health-fill" style={{ width: `${value}%`, background: color }} />
//             </div>
//             <span className="pm-health-pct" style={{ color }}>{value}%</span>
//         </div>
//     );
// }

export default function HrProjectManagement() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [productUsers, setProductUsers] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [openMenu, setOpenMenu] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, loading: false });

    // Product modal
    const [showModal, setShowModal] = useState(false);
    const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
    const [editingId, setEditingId] = useState(null);

    const fetchProducts = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const res = await api.get('/product/getAllProducts');
            setProducts(Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []));
            const arr = Array.isArray(res.data) ? res.data : (res.data?.data || res.data?.content || []);
            arr.forEach(p => fetchProductUsers(p.id));
        } catch { setError('Failed to load products.'); }
        finally { setLoading(false); }
    }, []);

    const fetchProductUsers = async (productId) => {
        try {
            const res = await api.get(`/product/${productId}/users`);
            const raw = res.data;
            const usersArr = Array.isArray(raw) ? raw : (Array.isArray(raw?.data) ? raw.data : (Array.isArray(raw?.content) ? raw.content : []));
            setProductUsers(prev => ({ ...prev, [productId]: usersArr }));
        } catch { setProductUsers(prev => ({ ...prev, [productId]: [] })); }
    };

    useEffect(() => { fetchProducts(); }, [fetchProducts]);

    // Navigate to detail — store product in sessionStorage so detail page can read it
    const openProduct = (product) => {
        sessionStorage.setItem(`product_${product.id}`, JSON.stringify(product));
        navigate(`/hr/products/${product.id}`);
    };

    // ── Product CRUD ──
    const openAdd = () => { setProductForm(EMPTY_PRODUCT); setEditingId(null); setShowModal(true); };
    const openEdit = (p, e) => {
        e.stopPropagation();
        setProductForm({ ...p, clientName: p.clientName || { name: '' }, manager_id: p.manager_id || { id: '' } });
        setEditingId(p.id);
        setShowModal(true);
        setOpenMenu(null);
    };

    const saveProduct = async () => {
        try {
            const body = { ...productForm };
            if (editingId) body.id = editingId;
            await api.post('/product/createProduct', body);
            setShowModal(false);
            fetchProducts();
        } catch { toast.error('Failed to save product.'); }
    };

    const deleteProduct = async (id, e) => {
        e.stopPropagation();
        setDeleteConfirm({ open: true, id });
    };

    const confirmDeleteProduct = async () => {
        const id = deleteConfirm.id;
        setDeleteConfirm(prev => ({ ...prev, loading: true }));
        try { await api.delete(`/product/deleteProduct/${id}`); fetchProducts(); toast.success('Product deleted.'); }
        catch { toast.error('Failed to delete product.'); }
        setOpenMenu(null);
        setDeleteConfirm({ open: false, id: null, loading: false });
    };

    const getHealth = (p) => {
        if (p.status === 'COMPLETED') return 100;
        if (p.status === 'ACTIVE') return Math.floor(65 + (p.id % 30));
        if (p.status === 'PLANNING') return Math.floor(20 + (p.id % 25));
        if (p.status === 'ON_HOLD') return Math.floor(30 + (p.id % 20));
        return 50;
    };

    const getTags = (p) => {
        const tags = [];
        if (p.clientName?.name) tags.push(p.clientName.name.toLowerCase().split(' ')[0]);
        if (p.status) tags.push(p.status.toLowerCase().replace('_', '-'));
        return tags.slice(0, 2);
    };

    const filtered = products.filter(p =>
        (p.productname || p.productName || p.name || '')?.toLowerCase().includes(search.toLowerCase()) ||
        (p.clientName?.name || '').toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div className="hr-content animate-fade-in">
            <ConfirmModal
                isOpen={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, id: null, loading: false })}
                onConfirm={confirmDeleteProduct}
                loading={deleteConfirm.loading}
                title="Delete Product?"
                message="This product and all its data will be permanently removed. This cannot be undone."
                confirmText="Delete"
                variant="danger"
            />
            <div className="pm-page" onClick={() => setOpenMenu(null)}>

                {/* Top bar */}
                <div className="pm-topbar">
                    <div>
                        <h1 className="pm-page-title">Products</h1>
                        <p className="pm-page-sub">Every product your engineering org owns.</p>
                    </div>
                    <div className="pm-topbar-right">
                        <div className="pm-search-wrap">
                            <FiSearch className="pm-search-icon" />
                            <input
                                className="pm-search"
                                placeholder="Search products..."
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                            {search && <FiX className="pm-search-clear" onClick={() => setSearch('')} />}
                        </div>
                        <button className="pm-new-btn" onClick={openAdd}>
                            <FiPlus /> New product
                        </button>
                    </div>
                </div>

                {error && <div className="pm-error"><FiAlertCircle /> {error}</div>}

                {loading ? (
                    <div className="pm-loading">Loading products…</div>
                ) : (
                    <div className="pm-grid">
                        {filtered.map((product) => {
                            const sm = STATUS_META[product.status] || STATUS_META.ACTIVE;
                            // const health = getHealth(product);
                            const users = productUsers[product.id] || [];
                            const tags = getTags(product);
                            const managerName = product.manager_id?.id || 'Unassigned';

                            return (
                                <div
                                    key={product.id}
                                    className="pm-card"
                                    onClick={() => openProduct(product)}
                                >
                                    {/* top row */}
                                    <div className="pm-card-top">
                                        <div className="pm-card-icon">
                                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10" />
                                                <path d="M8.56 2.75c4.37 6.03 6.02 9.42 8.03 17.72m2.54-15.38c-3.72 4.35-8.94 5.66-16.88 5.85m19.5 1.9c-3.5-.93-6.63-.82-8.94 0-2.58.92-5.01 2.86-7.44 6.32" />
                                            </svg>
                                        </div>
                                        <span className="pm-status-pill" style={{ color: sm.color, background: sm.bg }}>
                                            {sm.label}
                                        </span>
                                        <div className="pm-card-menu-wrap" onClick={e => e.stopPropagation()}>
                                            <button
                                                className="pm-menu-btn"
                                                onClick={() => setOpenMenu(openMenu === product.id ? null : product.id)}
                                            >
                                                <FiMoreVertical />
                                            </button>
                                            {openMenu === product.id && (
                                                <div className="pm-dropdown">
                                                    <button onClick={e => openEdit(product, e)}><FiEdit2 /> Edit</button>
                                                    <button className="danger" onClick={e => deleteProduct(product.id, e)}><FiTrash2 /> Delete</button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* name + desc */}
                                    <h3 className="pm-card-name">{product.productname || product.productName || product.name || 'Unnamed Product'}</h3>
                                    {product.description && <p className="pm-card-desc">{product.description}</p>}

                                    {/* meta */}
                                    <div className="pm-card-meta">
                                        <div className="pm-meta-row">
                                            <span className="pm-meta-label">Owner</span>
                                            <span className="pm-meta-value">{managerName}</span>
                                        </div>
                                    </div>

                                    {/* footer */}
                                    <div className="pm-card-footer">
                                        <div className="pm-avatars">
                                            {users.slice(0, 3).map((u, idx) => (
                                                <Avatar key={u.id} name={u.username} index={idx} size={30} />
                                            ))}
                                            {users.length > 3 && (
                                                <div className="pm-avatar pm-avatar-more" style={{ width: 30, height: 30, fontSize: 11 }}>
                                                    +{users.length - 3}
                                                </div>
                                            )}
                                            {users.length === 0 && <span className="pm-no-members">No members</span>}
                                        </div>
                                        <div className="pm-tags">
                                            {tags.map(tag => <span key={tag} className="pm-tag">{tag}</span>)}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {filtered.length === 0 && !loading && (
                            <div className="pm-empty-state">
                                <div className="pm-empty-icon">📦</div>
                                <p>No products found.</p>
                                <button className="pm-new-btn" onClick={openAdd}><FiPlus /> New product</button>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Product modal */}
            {showModal && (
                <div className="pm-modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="pm-modal" onClick={e => e.stopPropagation()}>
                        <div className="pm-modal-header">
                            <h3>{editingId ? 'Edit Product' : 'New Product'}</h3>
                            <button onClick={() => setShowModal(false)}><FiX /></button>
                        </div>
                        <div className="pm-modal-body">
                            <div className="pm-field">
                                <label>Product Name *</label>
                                <input value={productForm.productname} onChange={e => setProductForm({ ...productForm, productname: e.target.value })} />
                            </div>
                            <div className="pm-field">
                                <label>Client Name</label>
                                <input value={productForm.clientName?.name || ''} onChange={e => setProductForm({ ...productForm, clientName: { name: e.target.value } })} />
                            </div>
                            <div className="pm-field-row">
                                <div className="pm-field">
                                    <label>Budget (₹)</label>
                                    <input type="number" value={productForm.budget} onChange={e => setProductForm({ ...productForm, budget: e.target.value })} />
                                </div>
                                <div className="pm-field">
                                    <label>Manager ID</label>
                                    <input value={productForm.manager_id?.id || ''} onChange={e => setProductForm({ ...productForm, manager_id: { id: e.target.value } })} />
                                </div>
                            </div>
                            <div className="pm-field">
                                <label>Description</label>
                                <textarea rows={3} value={productForm.description} onChange={e => setProductForm({ ...productForm, description: e.target.value })} />
                            </div>
                            <div className="pm-field">
                                <label>Status</label>
                                <select value={productForm.status} onChange={e => setProductForm({ ...productForm, status: e.target.value })}>
                                    {['ACTIVE', 'INACTIVE', 'PLANNING', 'COMPLETED', 'MAINTENANCE'].map(s => <option key={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="pm-modal-footer">
                            <button className="pm-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                            <button className="pm-new-btn" onClick={saveProduct}>Save Product</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

